import ArrowDown from 'assets/svgs/arrow-down.svg';
import { DecoderText } from 'components/DecoderText';
import { Heading } from 'components/Heading';
import { Section } from 'components/Section';
import { useTheme } from 'components/ThemeProvider';
import { tokens } from 'components/ThemeProvider/theme';
import { Transition } from 'components/Transition';
import { VisuallyHidden } from 'components/VisuallyHidden';
import { AnimatePresence } from 'framer-motion';
import { useAppContext, useInterval, useScrollToHash } from 'hooks';
import dynamic from 'next/dynamic';
import RouterLink from 'next/link';
import { Fragment, useState } from 'react';
import { cssProps } from 'utils/style';
import styles from './Intro.module.css';


const MeshGradientBackground = dynamic(
  () => import('layouts/Home/MeshGradientBackground').then(mod => mod.MeshGradientBackground),
  { ssr: false }
);

const NorthernLightsBackground = dynamic(
  () => import('layouts/Home/NorthernLightsBackground').then(mod => mod.NorthernLightsBackground),
  { ssr: false }
);

const GrainGradientBackground = dynamic(
  () => import('layouts/Home/GrainGradientBackground').then(mod => mod.GrainGradientBackground),
  { ssr: false }
);


const GodRaysBackground = dynamic(
  () => import('layouts/Home/GodRaysBackground').then(mod => mod.GodRaysBackground),
  { ssr: false }
);


const ShaderBackground = ({ visible, shaderId, cropped }) => {
  switch (shaderId) {
    case 'northernLights':
      return <NorthernLightsBackground visible={visible} cropped={cropped} />;
    case 'grainGradient':
      return <GrainGradientBackground visible={visible} cropped={cropped} />;
    case 'godRays':
      return <GodRaysBackground visible={visible} cropped={cropped} />;
    case 'meshGradient':
    default:
      return <MeshGradientBackground visible={visible} cropped={cropped} />;
  }
};

export function Intro({ id, sectionRef, disciplines, scrollIndicatorHidden, ...rest }) {
  const { shader, shaderCropped } = useAppContext();
  const theme = useTheme();
  const [disciplineIndex, setDisciplineIndex] = useState(0);
  const [prevThemeId, setPrevThemeId] = useState(theme.themeId);
  const introLabel = [disciplines.slice(0, -1).join(', '), disciplines.slice(-1)[0]].join(
    ', and '
  );
  const currentDiscipline = disciplines.find((item, index) => index === disciplineIndex);
  const titleId = `${id}-title`;
  const scrollToHash = useScrollToHash();

  useInterval(
    () => {
      const index = (disciplineIndex + 1) % disciplines.length;
      setDisciplineIndex(index);
    },
    5000,
    theme.themeId
  );

  // Restart the discipline cycle when the theme changes
  if (prevThemeId !== theme.themeId) {
    setPrevThemeId(theme.themeId);
    setDisciplineIndex(0);
  }

  const handleScrollClick = event => {
    event.preventDefault();
    scrollToHash(event.currentTarget.href);
  };

  return (
    (<Section
      className={styles.intro}
      as="section"
      ref={sectionRef}
      id={id}
      aria-labelledby={titleId}
      tabIndex={-1}
      {...rest}
    >
      <Transition in timeout={3000}>
        {(visible, status) => (
          <Fragment>
            <ShaderBackground key={shader} visible={visible} shaderId={shader} cropped={shaderCropped} />
            <header className={styles.text}>
              <h1 className={styles.name} data-visible={visible} id={titleId}>
                <DecoderText text="Tim McDermott" delay={300} />
              </h1>
              <Heading level={0} as="h2" className={styles.title}>
                <VisuallyHidden className={styles.label}>
                  {`Engineer + ${introLabel}`}
                </VisuallyHidden>
                <span aria-hidden className={styles.row}>
                  <span
                    className={styles.word}
                    data-status={status}
                    style={cssProps({ delay: tokens.base.durationXS })}
                  >
                    Engineer
                  </span>
                  <span className={styles.line} data-status={status} />
                </span>
                <div className={styles.row} component="span">
                  <AnimatePresence>
                    {disciplines.map(item => (
                      <Transition
                        unmount
                        in={item === currentDiscipline}
                        timeout={{ enter: 3000, exit: 2000 }}
                        key={item}
                      >
                        {(visible, status) => (
                          <span
                            aria-hidden
                            className={styles.word}
                            data-plus={true}
                            data-status={status}
                            style={cssProps({ delay: tokens.base.durationL })}
                          >
                            {item}
                          </span>
                        )}
                      </Transition>
                    ))}
                  </AnimatePresence>
                </div>
              </Heading>
            </header>
            <RouterLink
              href="/#project-1"
              className={styles.scrollIndicator}
              data-status={status}
              data-hidden={scrollIndicatorHidden}
              onClick={handleScrollClick}>

              <VisuallyHidden>Scroll to projects</VisuallyHidden>

            </RouterLink>
            <RouterLink
              href="/#project-1"
              className={styles.mobileScrollIndicator}
              data-status={status}
              data-hidden={scrollIndicatorHidden}
              onClick={handleScrollClick}>

              <VisuallyHidden>Scroll to projects</VisuallyHidden>
              <ArrowDown aria-hidden />

            </RouterLink>
          </Fragment>
        )}
      </Transition>
    </Section>)
  );
}
